import * as React from 'react';
import ControllerApi from '@app/api/controller-api/controller.actions';
import GatewayApi from '@app/api/gateway-api/gateway.actions';
import Tunnel from '@app/api/tunnel/tunnel.actions';
import Server from '@app/api/gateway/gateway.actions';
import Site from '@app/api/site/site.actions';
import PublicIp from '@app/api/publicip/publicip.actions';
import Wan from '@app/api/wan/wan.actions';
import setControllerServers from '@app/pages/controller/ControllerHelper';
import { textInputRequiredOnly, isValidIP, isValidPrefix, isLengthWithinRange, isUUID } from '@app/lib/validator';
import { Timezones, getCountryOptions, getRegionOptions, getTimezoneOptions, getOffsetString } from '@app/common/world';
import { IconHeading } from '@app/components/IconHeading';
import { Saver } from '@app/components/Saver';
import CustomCard from '@app/components/CustomCard';
import ControllerIcon from '@app/bgimages/controllers-black.svg';
import { ExclamationCircleIcon, TrashIcon, PlusIcon, BullhornIcon, TopologyIcon, PficonNetworkRangeIcon } from '@patternfly/react-icons';
import { PageSection, Title, Checkbox, Flex, FlexItem, Modal, ModalVariant, TextInput, Select, SelectVariant, TextArea, Tabs, Tab, TabTitleText, TabTitleIcon, FormGroup } from "@patternfly/react-core";
import { Table, TableHeader, TableBody, TableVariant, cancelCellEdits, validateCellEdits, applyCellEdits, EditableTextCell } from '@patternfly/react-table';
import { FormGroupSpacer } from '../../components/forms/pf/form-group-spacer.component';
import classnames from 'classnames';

import './Controller.css';

import { connect } from 'react-redux';
import { actionCreators } from '@app/common/session/session.reducer';
import ThemeContext from '@app/providers/contexts/ThemeContext';
import Page from '@app/components/Page';
import { Col, Row, TabContent, TabPane, Nav, NavItem, NavLink, Button, Label, Input, Card, CardBody, CardHeader } from 'reactstrap';
import { MdAccountTree, MdAddCircle, MdAltRoute, MdDomain, MdLocationOn } from 'react-icons/md';
import { withRouter } from 'react-router-dom';
import Loader from '@app/components/Loader';
import { addControllerToCache, updateControllerInCache } from '@app/utils/cacheUtils';

// Declare Google Maps global variable
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

enum TabIndex {
  OSPF,
  BGP
}

const TitleMap = {
  [TabIndex.OSPF]: 'OSPF',
  [TabIndex.BGP]: 'BGP'
};

export const ControllerForm = connect()(class extends React.Component<any, any> {
  addressSearchTimeout: any = null;
  addressCache: Map<string, any[]> = new Map();
  autocompleteService: any = null;

  constructor(props) {
    super(props);

    const id = this.props.computedMatch?.params?.id;
    const countries = getCountryOptions();
    const timezones = getTimezoneOptions();

    const basePath = process.env.REACT_APP_BASE_PATH || '';  

    this.state = {
      id: id,
      name: "",
      description: "",
      address_1: "",
      address_2: "",
      city: "",
      state: "",
      country: "",
      postal_code: "",
      ip_address: "",
      routingEnabled: false,
      routingConfig: {
        ospf_enabled: false,
        ospf_configuration: "",
        bgp_enabled: false,
        bgp_configuration: ""
      },
      activeTabIndex: TabIndex.OSPF,
      isDeleteServerConfirmOpen: false,
      serverId: "",
      newServerTempId: -1,
      servers: [],
      bgp_configuration_rows: 10,
      ospf_configuration_rows: 10,
      message: "",
      messageType: "",
      editRowMode: false,
      mode: id ? "edit" : "new",
      activeTab: '1',
      isLoading: true,
      basePath: basePath,
      // Address autofill state
      addressSuggestions: [],
      showAddressSuggestions: false,
      isLoadingAddressSuggestions: false,
    };
  }

  static contextType = ThemeContext;

  toggleTab = (tab) => {
    if (this.state.activeTab !== tab) {
      this.setState({ activeTab: tab });
    }
  };

  handleOspfCheckboxChange = () => {
    this.setState(prevState => ({
      routingConfig: {
        ...prevState.routingConfig,
        ospf_enabled: !prevState.routingConfig.ospf_enabled
      }
    }));
  };

  handleBGPCheckboxChange = () => {
    this.setState(prevState => ({
      routingConfig: {
        ...prevState.routingConfig,
        bgp_enabled: !prevState.routingConfig.bgp_enabled
      }
    }));
  };

  handleOspfTextChange = (e) => {
    this.setState(prevState => ({
      routingConfig: {
        ...prevState.routingConfig,
        ospf_configuration: e.target.value
      }
    }));
  };

  handleBGPTextChange = (e) => {
    this.setState(prevState => ({
      routingConfig: {
        ...prevState.routingConfig,
        bgp_configuration: e.target.value
      }
    }));
  };

  componentDidMount = async () => {
    if (this.state.mode === "edit") {
      try {
        await this.loadController();
      } 
      catch (error) {
        this.setState(() => ({
          message: "There was an error loading the controller"
        }));
      }
    } else {
      this.setState(() => ({
        isLoading: false
      }));
    }
    
    // Load Google Maps for address autofill
    this.loadGoogleMapsScript();
  }

  componentWillUnmount() {
    if (this.addressSearchTimeout) {
      clearTimeout(this.addressSearchTimeout);
    }
  }

  loadController = async () => {
    const sites = await Site.getAll();
    const wans = await Wan.getAll();
    const controller = await ControllerApi.get(this.state.id);
    const gateways = await GatewayApi.getAll();
    const tunnels = await Tunnel.getAll();
    const publicIps = await PublicIp.getAll();

    if (controller === undefined) {
      this.props.history.push("/notfound");
    }
    

    controller.Servers = gateways.filter(gateway => gateway.controller_id === this.state.id).map(gateway => ({
      ...gateway,
      gatewayId: gateway.controller_id || gateway.gatewayId, // Ensure gatewayId is set
      ip4Address: gateway.ip_4_address || gateway.ip4Address 
    }));

    for (let i=0; i<controller.Servers.length; i++) {
      controller.Servers[i] = { 
        ...controller.Servers[i], 
        Tunnels: tunnels.filter(tunnel => tunnel.serverId === controller.Servers[i].id),
        PublicIps: publicIps.filter(publicIp => publicIp.serverId === controller.Servers[i].id)
      };
    }

    const servers = setControllerServers(controller, sites, wans);
    const assignedServers = servers['assigned'].map(item => { return this.getRow(item, false); });
    const availableServers = servers['available'].map(item => { return this.getRow(item, false); });
    const regions = getRegionOptions(controller.country) || [];
    const timezones = Timezones();
    // Find timezone by matching the formatted string, not the name property
    const timezoneObj = timezones.find(t => getOffsetString(t) === controller.timezone);
    // Null check to prevent error when timezone is not found
    const timezone = timezoneObj ? getOffsetString(timezoneObj) : controller.timezone || '';
    let ospfConfig = "";
    let bgpConfig = "";
    const ospf = controller.routing_configuration?.ospf_configuration;
    const bgp = controller.routing_configuration?.bgp_configuration;
    
    if (ospf) {
      ospfConfig = ospf.join('\n');
    }
    if (bgp) {
      bgpConfig = bgp.join('\n');
    }

    this.setState(() => ({
      name: controller.name,
      description: controller.description,
      address_1: controller.address?.address_1 || '',
      address_2: controller.address?.address_2 || '',
      city: controller.address?.city || '',
      state: controller.address?.state || '',
      country: controller.address?.country || '',
      postal_code: controller.address?.postal_code || '',
      ip_address: controller.ip_address || '',
      // routerIp: controller.router_ip,
      // isVps: controller.is_vps,
      // vpsProvider: controller.vps_provider,
      // monitorEndpoint: controller.monitor_endpoint,
      ospf_configuration_rows: ospf?.length > 10 ? ospf.length : 10,
      bgp_configuration_rows: bgp?.length > 10 ? bgp.length : 10,
      routingEnabled: controller.dynamic_routing_enabled,
      routingConfig: {
        ospf_enabled: controller.routing_configuration?.ospf_enabled,
        ospf_configuration: ospfConfig,
        bgp_enabled: controller.routing_configuration?.bgp_enabled,
        bgp_configuration: bgpConfig,
      },
      servers: [...assignedServers, ...availableServers],
      isLoading: false
    }));
  }

  getRow = (server, isEditable) => {
    var cells: any[] = [];

    cells.push(this.getCell(server, "ip_4_address"));
    cells.push(this.getCell(server, "ip_4_cidr"));
    cells.push(this.getCell(server, "ip_4_router"));
    cells.push(this.getCell(server, "delete"));
    cells.push(this.getCell(server, "id"));

    const row = {
      rowEditValidationRules: [{
        name: 'required',
        validator: value => value.trim() !== '',
        errorText: 'Field required'
      },{
        name: 'invalidIp',
        validator: value => isValidIP(value.trim()) !== false,
        errorText: 'Invalid IP Address'
      },{
        name: 'invalidMask',
        validator: value => isValidPrefix(value.trim()) !== false,
        errorText: 'Must be between /8 and /32'
      }],
      cells: cells,
      isEditable: false,
      publicIpId: (server.PublicIps && server.PublicIps.length > 0) ? server.PublicIps[0].id : ""
    }

    row.isEditable = isEditable;
 
    return row;
  } 

  getCell = (server, columnName) => {
    if (columnName === 'delete') {
      return { 
        title: (
          <div className="trash-icon" onClick={(e) => this.deleteConfirmation(e, server.id)}>
            <TrashIcon />
          </div>
        ),
        props: {
          value: "-1",
          name: columnName
        }
      };
    }
    
    const cell = {
      title: (value, rowIndex, cellIndex, props) => (
        <EditableTextCell
          value={value}
          rowIndex={rowIndex}
          cellIndex={cellIndex}
          props={props}
          handleTextInputChange={this.handleTextInputChange}
          inputAriaLabel={columnName}
        />
      ),
      props: {
        value: server[columnName],
        name: columnName
      }
    }

    return cell;
  }

  handleTabClick = (e, index) => {
    this.setState(() => ({
      activeTabIndex: index
    }));
  };

  addServer = () => {
    const { servers, newServerTempId } = this.state;
    var emptyRow = { ip_4_address: "", ip_4_cidr: "/24", ip_4_router: "", delete: "", id: newServerTempId.toString() };

    servers.push(this.getRow(emptyRow, true));

    this.setState(() => ({
      newServerTempId: newServerTempId - 1,
      editRowMode: true,
      servers: servers
    }));
  }

  deleteConfirmation = (event, id) => {

    this.deleteServer(id);
  }

  deleteServer = async (id?: string) => {
    const { serverId, servers } = this.state;
    const serverIdToDelete = id || serverId;
    const serverIdIndex = this.getServerColumns().length;
    const updated = servers.filter(c => c.cells[serverIdIndex].props.value !== serverIdToDelete);

    // If server being deleted actually exists in the DB. Servers added, but not persisted will have "-1" id
    if (isUUID(serverIdToDelete)) {
      try {
        await Server.delete(serverIdToDelete);
        this.setState(() => ({
          servers: updated, 
        }));
      } catch (error) {
        if ((error as any).message === 'SERVER_ASSIGNED') {
          this.props.dispatch(actionCreators.notificationEnqueue({
            message: 'Server Assigned: Unable to Delete',
            options: {
              key: new Date().getTime() + Math.random(),
              variant: 'error',
            },
          }));
        }
      }
    }
    else {
      this.setState(() => ({
        servers: updated, 
      }));
    }

    this.setState(() => ({
      isDeleteServerConfirmOpen: false,
      serverId: "",
      error: "",
      editRowMode: false
    }));
  }

  onCountryToggle = (isOpen) => {
    this.setState(() => ({ 
      isCountryOpen: isOpen
    }));
  }

  onRegionToggle = (isOpen) => {
    this.setState(() => ({ 
      isRegionOpen: isOpen
    }));
  }

  onTimezoneToggle = (isOpen) => {
    this.setState(() => ({ 
      isTimezoneOpen: isOpen
    }));
  }

  onCountrySelect = (event, selection, isPlaceholder) => {
    if (isPlaceholder) {
      this.clearCountry();
    }
    else {
      const regions = getRegionOptions(selection);
      this.setState(() => ({ 
        country: selection,
        regions: regions,
        region: null,
        isCountryOpen: false 
      }));
    }
  }

  onRegionSelect = (event, selection, isPlaceholder) => {
    if (isPlaceholder) {
      this.clearRegion();
    }
    else {
      this.setState(() => ({ 
        region: selection,
        isRegionOpen: false 
      }));
    }
  }

  onTimezoneSelect = (event, selection, isPlaceholder) => {
    if (isPlaceholder) {
      this.clearTimezone();
    }
    else {
      this.setState(() => ({ 
        timezone: selection,
        isTimezoneOpen: false 
      }));
    }
  }

  clearCountry = () => {
    this.setState({
      country: null,
      isCountryOpen: false
    });
  };

  clearRegion = () => {
    this.setState({
      region: null,
      isRegionOpen: false
    });
  };

  clearTimezone = () => {
    this.setState({
      timezone: null,
      isTimezoneOpen: false
    });
  };

  handleTextInputChange = (newValue, evt, rowIndex, cellIndex) => {
    let newRows = Array.from(this.state.servers);
    //@ts-ignore
    newRows[rowIndex].cells[cellIndex].props.editableValue = newValue;
    this.setState(() => ({
      servers: newRows
    }));
  }

  updateEditableRows = async (evt, type, isEditable, rowIndex, validationErrors) => {
    let newRows = Array.from(this.state.servers);
    var invalidIps = [];
    var invalidMasks = [];

    if (validationErrors.invalidIp && validationErrors.invalidIp.includes("ip_4_address")) {
      invalidIps.push("ip_4_address")
    }    
    if (validationErrors.invalidIp && validationErrors.invalidIp.includes("ip_4_router")) {
      invalidIps.push("ip_4_router")
    }
    if (validationErrors.invalidMask && validationErrors.invalidMask.includes("ip_4_cidr")) {
      invalidMasks.push("ip_4_cidr")
    }

    if (invalidIps.length > 0) {
      validationErrors.invalidIp = invalidIps;
    }
    else {
      delete validationErrors.invalidIp
    }

    if (invalidMasks.length > 0) {
      validationErrors.invalidMask = invalidMasks;
    }
    else {
      delete validationErrors.invalidMask
    }

    if (validationErrors && Object.keys(validationErrors).length) {
      newRows[rowIndex] = validateCellEdits(newRows[rowIndex], type, validationErrors);
      this.setState({ servers: newRows });
      return;
    }
    
    if (type === 'edit' && Object.keys(validationErrors).length === 0) {
      this.setState({ editRowMode: true });
      newRows[rowIndex] = applyCellEdits(newRows[rowIndex], type);
      return;
    }

    if (type === 'cancel') {
      newRows[rowIndex] = cancelCellEdits(newRows[rowIndex]);
      this.setState({ servers: newRows, editRowMode: false });
      return;
    }
    
    if (type === 'save') {
      newRows[rowIndex] = applyCellEdits(newRows[rowIndex], type);

      // Check for duplicate IPs
      // @ts-ignore
      let currentRowIp = newRows[rowIndex].cells[0].props.value;
      // @ts-ignore
      let newServerIps = newRows.filter(s => s.cells[0].props.value === currentRowIp);

      if (newServerIps.length > 1) {
        this.setState({ editRowMode: true, error: currentRowIp + " already exists. Please edit and try another" });
      }
      else {
        this.setState({ servers: newRows, editRowMode: false, error: "" });
      }
    }
  }

  getServerColumns = () => {
    return ['IP Address', 'Subnet', 'LXD Bridge IP', ''];
  }

  handleConfirmToggle = () => {
    this.setState(({ isDeleteServerConfirmOpen }) => ({
      isDeleteServerConfirmOpen: !isDeleteServerConfirmOpen
    }));
  };

  handleChange = (name, value) => {
    this.setState(() => ({ 
      [name]: value 
    }));
  }

  loadGoogleMapsScript() {
    // Check if Google Maps is already loaded
    if (window.google && window.google.maps) {
      this.initGoogleMaps();
      return;
    }

    // Check if script is already being loaded
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      // Script is already loading, wait for it
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          this.initGoogleMaps();
        }
      }, 100);
      return;
    }

    // Load Google Maps script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&region=CA`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.initGoogleMaps();
    };
    document.head.appendChild(script);
  }

  initGoogleMaps() {
    if (window.google && window.google.maps && window.google.maps.places) {
      this.autocompleteService = new window.google.maps.places.AutocompleteService();
    }
  }

  // Parse Google Place details into our address format
  parseGooglePlaceDetails(place: any) {
    const components = place.address_components || [];
    
    const getComponent = (types: string[]) => {
      const component = components.find((comp: any) => 
        types.some((type: string) => comp.types.includes(type))
      );
      return component ? component.long_name : '';
    };

    let state = getComponent(['administrative_area_level_1']);
    let country = getComponent(['country']);

    // Handle Canadian provinces - normalize Quebec variations
    if (country === 'Canada' || country === 'CA') {
      country = 'Canada';
      
      // Handle Quebec variations (French/English)
      if (state === 'Québec' || state === 'Quebec' || state === 'QC') {
        state = 'Quebec';
      }
      // Handle other Canadian provinces
      else if (state === 'Ontario' || state === 'ON') {
        state = 'Ontario';
      }
      else if (state === 'British Columbia' || state === 'BC') {
        state = 'British Columbia';
      }
      else if (state === 'Alberta' || state === 'AB') {
        state = 'Alberta';
      }
      // Add more province mappings as needed
    }

    return {
      house_number: getComponent(['street_number']),
      road: getComponent(['route']),
      city: getComponent(['locality', 'sublocality', 'administrative_area_level_3']),
      state: state,
      country: country,
      postcode: getComponent(['postal_code'])
    };
  }

  // Google Places search
  async searchWithGooglePlaces(query: string) {
    if (!this.autocompleteService) {
      this.setState({
        addressSuggestions: [],
        showAddressSuggestions: false,
        isLoadingAddressSuggestions: false,
      });
      return;
    }

    try {
      const request = {
        input: query,
        types: ['address'],
      };

      this.autocompleteService.getPlacePredictions(request, (predictions: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const suggestions = predictions.map((prediction: any) => ({
            display_name: prediction.description,
            place_id: prediction.place_id,
            address: {},
            isGooglePlace: true 
          }));

          this.addressCache.set(query.toLowerCase().trim(), suggestions);
          
          this.setState({
            addressSuggestions: suggestions,
            showAddressSuggestions: suggestions.length > 0,
            isLoadingAddressSuggestions: false,
          });
        } else {
          this.setState({
            addressSuggestions: [],
            showAddressSuggestions: false,
            isLoadingAddressSuggestions: false,
          });
        }
      });

    } catch (error) {
      console.error('Google Places error:', error);
      this.setState({
        addressSuggestions: [],
        showAddressSuggestions: false,
        isLoadingAddressSuggestions: false,
      });
    }
  }

  // Get Google Place details
  async getGooglePlaceDetails(placeId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps) {
        reject(new Error('Google Maps not loaded'));
        return;
      }

      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails({
        placeId: placeId,
        fields: ['address_components', 'formatted_address']
      }, (place: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          resolve(place);
        } else {
          reject(new Error(`Place details request failed: ${status}`));
        }
      });
    });
  }

  selectAddressSuggestion(suggestion: any) {
    // Handle Google Places selection
    if (suggestion.isGooglePlace && suggestion.place_id) {
      this.setState({ isLoadingAddressSuggestions: true });
      
      this.getGooglePlaceDetails(suggestion.place_id)
        .then((place) => {
          const address = this.parseGooglePlaceDetails(place);
          this.fillAddressFields(suggestion.display_name, address);
        })
        .catch((error) => {
          console.error('Error getting place details:', error);
          this.fillAddressFields(suggestion.display_name, {});
        })
        .finally(() => {
          this.setState({ isLoadingAddressSuggestions: false });
        });
    } else {
      this.fillAddressFields(suggestion.display_name, suggestion.address || {});
    }

    this.setState({
      addressSuggestions: [],
      showAddressSuggestions: false,
    });
  }

  // Fill address fields with selected suggestion
  fillAddressFields(displayName: string, address: any) {
    // Extract address components
    const streetNumber = address.house_number || '';
    const streetName = address.road || '';
    let fullAddress = '';
    
    if (streetNumber && streetName) {
      fullAddress = `${streetNumber} ${streetName}`;
    } else if (streetName) {
      fullAddress = streetName;
    } else {
      fullAddress = displayName.split(',')[0].trim();
    }
    
    const city = address.city || '';
    const state = address.state || '';
    const country = address.country || '';
    const postalCode = address.postcode || '';

    // Update address fields
    this.setState({
      address_1: fullAddress,
      city: city,
      state: state,
      country: country,
      postal_code: postalCode,
    });
  }

  hideAddressSuggestions() {
    setTimeout(() => {
      this.setState({ showAddressSuggestions: false });
    }, 200);
  }

  // Address autocomplete functionality
  async searchAddress(query: string) {
    if (!query || query.length < 1) {
      this.setState({
        addressSuggestions: [],
        showAddressSuggestions: false,
        isLoadingAddressSuggestions: false,
      });
      return;
    }

    // Check cache first
    const cacheKey = query.toLowerCase().trim();
    if (this.addressCache.has(cacheKey)) {
      const cachedResults = this.addressCache.get(cacheKey) || [];
      this.setState({
        addressSuggestions: cachedResults,
        showAddressSuggestions: cachedResults.length > 0,
        isLoadingAddressSuggestions: false,
      });
      return;
    }

    this.setState({ isLoadingAddressSuggestions: true });
    
    await this.searchWithGooglePlaces(query);
  }

  handleAddressChange = (value: string) => {
    this.setState({ address_1: value });

    if (this.addressSearchTimeout) {
      clearTimeout(this.addressSearchTimeout);
    }

    this.addressSearchTimeout = setTimeout(() => {
      this.searchAddress(value);
    }, 50);
  }

  handleObjectChange = (key, name, value) => {
    const obj = this.state[key];
    const rowsKey = name + '_rows';
    const configRows = obj[name].split('\n');
    const numRows = configRows.length > 10 ? configRows.length : 10;
    
    // Fix for [object Object] issue
    if (typeof value === 'object') {
      value = value.target.value;
    }
    
    obj[name] = value;
    
    this.setState(() => ({
      [key]: obj,
      [rowsKey]: numRows
    }));
  };

  handleConfigPaste = (key, event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('Text');
    const configProp = key.replace('_rows', '');
    const configRows = pastedData.split('\n');
    const numRows = configRows?.length > 10 ? configRows?.length : 10;
    const config = this.state.routingConfig;
    config[configProp] = pastedData;

    this.setState(() => ({
      routingConfig: { ...config },
      [key]: numRows
    }));
  };

  handleRoutingCheckboxChange = (checked, event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    this.setState(prevState => ({
      routingConfig: {
        ...prevState.routingConfig,
        [name]: value
      },
    }));
  };

  handleCancel = () => {
    const { id, basePath } = this.state;
    
    // Set loading state before navigation to prevent white screen
    this.setState({ isLoading: true }, () => {
      // Use timeout to ensure state update is processed
      setTimeout(() => {
        // Use replaced method to avoid navigation issues
        if (id) {
          this.props.history.push(`${basePath}/controller/${id}`);
        }
        else {
          this.props.history.push(`${basePath}/controllers`);
        }
      }, 0);
    });
  }

  handleSubmit = async () => {
    if (this.state.mode === "edit") {
      this.updateController();
    }
    else if (this.state.mode === "new") {
      this.createController();
    }
  }

  getPayloadFromState = () => {
    const routingConfig = this.state.routingConfig;

    // Create payload object with address
    const payload = {
      name: this.state.name,
      description: this.state.description,
      address: {
        address_1: this.state.address_1 || '',
        address_2: this.state.address_2 || '',
        city: this.state.city || '',
        state: this.state.state || '',
        country: this.state.country || '',
        postal_code: this.state.postal_code || '',
      },
      gateways: this.state.servers,
      ip_address: this.state.ip_address,
      dynamic_routing_enabled: routingConfig.ospf_enabled || routingConfig.bgp_enabled,
      routing_configuration: {
        ospf_enabled: routingConfig.ospf_enabled,
        ospf_configuration: routingConfig.ospf_configuration?.split('\n') || [],
        bgp_enabled: routingConfig.bgp_enabled,
        bgp_configuration: routingConfig.bgp_configuration?.split('\n') || []
      }
    };

    return payload;
  }

  createController = async () => {
    const { name, servers, basePath } = this.state;
    
    // Set loading state to true
    this.setState({ isLoading: true });

    if (name.toLowerCase() === "serverless") {
      this.setState(() => ({
        message: "The name " + name + " is reserved. Please try another name",
        isLoading: false // Reset loading state
      })); 
      return;
    }
    
    try {
      const controllers = await ControllerApi.getAll() || [];
      // Check if a controller with this name already exists
      const duplicateController = Array.isArray(controllers) && controllers.find(c => c.name === name);

      if (duplicateController) {
        this.setState(() => ({
          message: "A controller with the name " + name + " already exists. Please try another name",
          isLoading: false // Reset loading state
        })); 
        return;
      }

      const payload = this.getPayloadFromState();
      let result = await ControllerApi.create(payload);
      
      try {
        
        // Fetch controller data 
        const newController = await ControllerApi.get(result.id);
        const gateways = await GatewayApi.getAll();
        const tunnels = await Tunnel.getAll();
        
        // Build complete Servers array with Tunnels
        newController.Servers = gateways.filter(gateway => gateway.controller_id === result.id).map(gateway => ({
          ...gateway,
          gatewayId: gateway.controller_id || gateway.gatewayId, // Ensure gatewayId is set
          ip4Address: gateway.ip_4_address || gateway.ip4Address 
        }));
        for (let i = 0; i < newController.Servers.length; i++) {
          newController.Servers[i] = { 
            ...newController.Servers[i], 
            Tunnels: tunnels.filter(tunnel => tunnel.serverId === newController.Servers[i].id),
          };
        }
        
        try {
          const DashboardApi = await import('@app/api/dashboard-api/dashboard.actions');
          const dashboardData = await DashboardApi.default.getInfo();
          const dashboardController = dashboardData?.partner?.controllers?.controllers?.find(
            (c: any) => c.id === result.id
          );
          if (dashboardController?.status) {
            newController.status = dashboardController.status;
          } else {
            newController.status = 0;
          }
        } catch (dashboardError) {
          console.warn('ControllerForm: Could not fetch dashboard status, using default');
          newController.status = 0; 
        }
        
        // Add to cache with complete controller data including Servers
        addControllerToCache(newController);
      } catch (cacheError) {
        console.error('ControllerForm: Failed to add controller to cache with server data', cacheError);
        
        try {
          const controllerForCache = {
            id: result.id,
            name: result.name,
            description: result.description,
            country: result.country,
            region: result.region,
            status: 0, // Unknown 
            displayName: result.name,
            Servers: [], // Empty array to prevent undefined errors
          };
          addControllerToCache(controllerForCache);
        } catch (fallbackError) {
          console.error('ControllerForm: Complete failure to add controller to cache', fallbackError);
        }
      }
      
      // Use React Router navigation instead of window.location to prevent full reload
      this.props.history.push(`${basePath}/controller/${result.id}`);
    }
    catch (error) {
      this.setState(() => ({
        message: "There was an error creating the controller",
        isLoading: false // Reset loading state
      }));
    }
  }

  updateController = async () => {
    const { name, servers, basePath, id } = this.state;
    
    // Set loading state to true
    this.setState({ isLoading: true });

    if (name.toLowerCase() === "serverless") {
      this.setState(() => ({
        message: "The name " + name + " is reserved. Please try another name",
        isLoading: false // Reset loading state
      })); 
      return;
    }

    try {
      const controllers = await ControllerApi.getAll() || [];
      // Filter out the current controller from the list (so it doesn't find itself)
      const otherControllers = Array.isArray(controllers) ? controllers.filter(c => c.id !== this.state.id) : [];
      // Check if any other controller has the same name
      const duplicateController = otherControllers.find(c => c.name === name);

      if (duplicateController) {
        this.setState(() => ({
          message: "A controller with the name " + name + " already exists. Please try another name",
          isLoading: false // Reset loading state
        })); 
        return;
      }

      const payload = this.getPayloadFromState();
      let result = await ControllerApi.update(id, payload);
      

      try {
        // Fetch controller data 
        const updatedController = await ControllerApi.get(id);
        const gateways = await GatewayApi.getAll();
        const tunnels = await Tunnel.getAll();
        
        // Build complete Servers array with Tunnels 
        updatedController.Servers = gateways.filter(gateway => gateway.controller_id === id).map(gateway => ({
          ...gateway,
          gatewayId: gateway.controller_id || gateway.gatewayId, // Ensure gatewayId is set
          ip4Address: gateway.ip_4_address || gateway.ip4Address 
        }));
        
        for (let i = 0; i < updatedController.Servers.length; i++) {
          const serverTunnels = tunnels.filter(tunnel => tunnel.serverId === updatedController.Servers[i].id);
          updatedController.Servers[i] = { 
            ...updatedController.Servers[i], 
            Tunnels: serverTunnels,
          };
        }
        
        try {
          const DashboardApi = await import('@app/api/dashboard-api/dashboard.actions');
          const dashboardData = await DashboardApi.default.getInfo();
          const dashboardController = dashboardData?.partner?.controllers?.controllers?.find(
            (c: any) => c.id === id
          );
          if (dashboardController?.status) {
            updatedController.status = dashboardController.status;
          }
        } catch (dashboardError) {
          console.warn('ControllerForm: Could not fetch dashboard status, using controller data only');
        }
        
        updateControllerInCache(updatedController);
      } catch (cacheError) {
        console.error('ControllerForm: Failed to update controller cache with server data', cacheError);
        
        try {
          const updatedControllerForCache = {
            id: id,
            name: result.name,
            description: result.description,
            country: result.country,
            region: result.region,
            displayName: result.name,
          };
          updateControllerInCache(updatedControllerForCache);
        } catch (fallbackError) {
          console.error('ControllerForm: Complete failure to update controller in cache', fallbackError);
        }
      }
      
      // Use React Router navigation instead of window.location to prevent full reload
      this.props.history.push(`${basePath}/controller/${id}`);
    }
    catch (error) {
      this.setState(() => ({
        message: "There was an error updating the controller",
        isLoading: false // Reset loading state
      }));
    }
  }

  render() {
    const { 
      name, description, address_1, city, state, country, postal_code, ip_address, routingConfig, routingEnabled, serverId,
      bgp_configuration_rows, ospf_configuration_rows, activeTabIndex, isDeleteServerConfirmOpen, servers, editRowMode, mode
    } = this.state;

    const bgpErrorState = routingConfig?.bgp_enabled && routingConfig?.bgp_configuration === "" ? { validated: 'error', errorText: " BGP Configuration must be provided" } : { validated: 'default', errorText: "" };
    const ospfErrorState = routingConfig?.ospf_enabled && routingConfig?.ospf_configuration === "" ? { validated: 'error', errorText: " OSPF Configuration must be provided" } : { validated: 'default', errorText: "" };
    const descriptionErrorState = !description?.trim() ? { validated: 'error', errorText: 'Required field' } 
      : !isLengthWithinRange(description, 1, 255) ? { validated: 'error', errorText: " Max 255 characters allowed" } 
      : { validated: 'default', errorText: "" };
    const ipAddressErrorState = !ip_address?.trim() ? { errorText: 'Required field', validated: 'error' } 
      : !isValidIP(ip_address.trim()) ? { errorText: 'Invalid IP Address', validated: 'error' }
      : { errorText: '', validated: 'default' };
    const addressErrorState = !address_1?.trim() ? { validated: 'error', errorText: 'Required field' } : { validated: 'default', errorText: '' };
    const nameErrorState = !name?.trim() ? { validated: 'error', errorText: 'Required field' } : { validated: 'default', errorText: '' };

    const formInvalid = !name || !address_1 || !city || !country || editRowMode || nameErrorState.validated === 'error' || 
      descriptionErrorState.validated === 'error' || bgpErrorState.validated === 'error' || 
      ospfErrorState.validated === 'error' || ipAddressErrorState.validated === 'error' || addressErrorState.validated === 'error';
    const { theme } = this.context;
    const { activeTab, ospfChecked, bgpChecked, ospfText, bgpText, addressSuggestions, showAddressSuggestions, isLoadingAddressSuggestions } = this.state;

    return (
      <React.Fragment>
        {this.state.isLoading ? ( 
          <Loader />
        ) : (
          <Page
            tag="div"
            className={`cr-page px-3 pt-2 controllers-form ${theme}`}
            title=""
            breadcrumbs={[
              { name: "Controllers", url: "/controllers", active: false },
              { name: this.state.mode === "edit" ? "Edit Controller" : "New Controller", active: true },
            ]}
            actionButton={null}
          >
            <Row>
              <Col lg={12} md={12} sm={12} xs={12}>
                <div className="controllers-card-wrapper d-flex" style={{ gap: '16px' }}>
                  <div style={{ flexBasis: 'calc(25% - 11px)' }}>
                    <CustomCard isCompact className="controller-edit-panel rounded-2 h-100">
                      <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                        <div className="w-100">                  
                          <IconHeading
                            icon={<MdDomain className="icon-medium" />}
                            heading="Controller"
                          />
                        </div>
                      </CardHeader>
                      <CardBody className="pt-3">
                        <FormGroup label="Controller Name:" required id="name" className="form-item"
                          helperTextInvalid={nameErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={nameErrorState.validated as any}>
                          <TextInput
                            required type="text" id="name" name="name" autoComplete="new-password" className="form-control"
                            value={name || ''} onChange={(e) => this.handleChange("name", e)}
                            style={{height:"38px"}} placeholder="Controller Name" validated={nameErrorState.validated as any}
                          />

                        </FormGroup>

                        <FormGroup label="IP Address:" id="ip_address" className="form-item" required
                          helperTextInvalid={ipAddressErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={ipAddressErrorState.validated as any}>
                          <TextInput
                            required type="text" id="ip_address" name="ip_address" autoComplete="new-password" className="form-control"
                            value={ip_address || ''} onChange={(e) => this.handleChange("ip_address", e)}
                            style={{height:"38px"}} placeholder="IP Address" validated={ipAddressErrorState.validated as any}
                          />
                        </FormGroup>

                        <FormGroupSpacer>
                          <FormGroup label="Description:" id="description" required className="form-item"
                            helperTextInvalid={descriptionErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={descriptionErrorState.validated as any}>
                            <TextArea 
                              rows={3} id="description" name="description" autoComplete="new-password"                              
                              value={description || ''} onChange={(e) => this.handleChange("description", e)} 
                              aria-label="Controller description" className="form-control" placeholder="Description" validated={descriptionErrorState.validated as any}
                            />
                          </FormGroup>
                        </FormGroupSpacer>
                      </CardBody>
                    </CustomCard>
                  </div>

                  <div style={{ flexBasis: 'calc(25% - 11px)' }}>
                    <CustomCard isCompact className="controller-edit-panel rounded-2 h-100">
                      <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                        <div className="w-100">                  
                          <IconHeading
                            icon={<MdLocationOn className="icon-medium" />}
                            heading="Address"
                          />
                        </div>
                      </CardHeader>
                      <CardBody className="pt-3">
                        <FormGroup label="Address Line 1" className="form-item" style={{ position: 'relative' }} required
                          helperTextInvalid={addressErrorState.errorText} helperTextInvalidIcon={<ExclamationCircleIcon />} validated={addressErrorState.validated as any}>
                          <Input
                            type="text" 
                            value={this.state.address_1 || ''}
                            onChange={(e) => this.handleAddressChange(e.target.value)}
                            onBlur={this.hideAddressSuggestions}
                            placeholder="Start typing address..."
                            autoComplete="off"
                          />
                          
                          {/* Address Suggestions Dropdown */}
                          {this.state.showAddressSuggestions && (
                            <div 
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #ccc',
                                borderRadius: '4px',
                                maxHeight: '200px',
                                overflowY: 'auto',
                                zIndex: 1000,
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                              }}
                            >
                              {this.state.isLoadingAddressSuggestions ? (
                                <div style={{ padding: '12px', textAlign: 'center', color: '#666' }}>
                                  Loading addresses...
                                </div>
                              ) : (
                                this.state.addressSuggestions.map((suggestion, index) => (
                                  <div
                                    key={index}
                                    style={{
                                      padding: '12px',
                                      cursor: 'pointer',
                                      borderBottom: index < this.state.addressSuggestions.length - 1 ? '1px solid #eee' : 'none'
                                    }}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => this.selectAddressSuggestion(suggestion)}
                                    onMouseEnter={(e) => {
                                      if (e.currentTarget) {
                                        e.currentTarget.style.backgroundColor = '#f8f9fa';
                                      }
                                    }}
                                    onMouseLeave={(e) => {
                                      if (e.currentTarget) {
                                        e.currentTarget.style.backgroundColor = 'white';
                                      }
                                    }}
                                  >
                                    <div style={{ fontSize: '14px', fontWeight: '500' }}>
                                      {suggestion.display_name}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </FormGroup>

                        <FormGroup label="Address Line 2" className="form-item">
                          <Input
                            type="text" 
                            value={this.state.address_2 || ''}
                            onChange={(e) => this.handleChange("address_2", e.target.value)}
                            placeholder="Address Line 2 (Optional)"
                          />
                        </FormGroup>

                        <FormGroup label="City / State" className="form-item">
                          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <div style={{ flex: '1 1 50%' }}>
                              <Input
                                type="text" 
                                value={this.state.city || ''}
                                onChange={(e) => this.handleChange("city", e.target.value)}
                                placeholder="City"
                              />
                            </div>
                            <div style={{ flex: '1 1 50%' }}>
                              <Input
                                type="text" 
                                value={this.state.state || ''}
                                onChange={(e) => this.handleChange("state", e.target.value)}
                                placeholder="State/Province"
                              />
                            </div>
                          </div>
                        </FormGroup>
                        
                        <FormGroup label="Country / Postal" className="form-item">
                          <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                            <div style={{ flex: '1 1 50%' }}>
                              <Input
                                type="text" 
                                value={this.state.country || ''}
                                onChange={(e) => this.handleChange("country", e.target.value)}
                                placeholder="Country"
                              />
                            </div>
                            <div style={{ flex: '1 1 50%' }}>
                              <Input
                                type="text" 
                                value={this.state.postal_code || ''}
                                onChange={(e) => this.handleChange("postal_code", e.target.value)}
                                placeholder="Postal Code"
                              />
                            </div>
                          </div>
                        </FormGroup>
                      </CardBody>
                    </CustomCard>
                  </div>

                  <div style={{ flexBasis: 'calc(50% - 10px)' }}>
                    <CustomCard isCompact className="rounded-2 h-100">
                      <CardHeader className="p-2 pb-0 fw-bold d-flex align-items-center bg-transparent">     
                        <div className="pb-2">
                          <IconHeading
                            icon={<MdAltRoute className="icon-medium" />}
                            heading="Dynamic Routing"
                          />
                        </div>
                        <div className="d-flex ms-2">
                          <Nav tabs>
                            <NavItem>
                              <NavLink
                                className={classnames({ active: activeTab === '1' })}
                                onClick={() => this.toggleTab('1')}
                              >
                                <span className="pb-1 pt-1">OSPF</span>
                              </NavLink>
                            </NavItem>
                            <NavItem>
                              <NavLink
                                className={classnames({ active: activeTab === '2' })}
                                onClick={() => this.toggleTab('2')}
                              >
                                <span className="pb-1 pt-1">BGP</span>                              
                              </NavLink>
                            </NavItem>
                          </Nav>
                        </div>
                      </CardHeader>
                      <CardBody className="pt-3">
                        <TabContent activeTab={activeTab}>
                          <TabPane tabId="1">
                            <FormGroup className="form-item">
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={routingConfig?.ospf_enabled}
                                  onChange={this.handleOspfCheckboxChange}
                                  className="me-1"
                                />
                                Enable OSPF
                              </Label>
                            </FormGroup>
                            <FormGroup className="form-item">
                              <Label for="ospfTextarea">OSPF Details</Label>
                              <Input
                                type="textarea"
                                id="ospfTextarea"
                                value={routingConfig?.ospf_configuration}
                                onChange={this.handleOspfTextChange}
                                disabled={!routingConfig?.ospf_enabled}
                              />
                            </FormGroup>
                          </TabPane>
                          <TabPane tabId="2">
                            <FormGroup className="form-item">
                              <Label check>
                                <Input
                                  type="checkbox"
                                  checked={routingConfig?.bgp_enabled}
                                  onChange={this.handleBGPCheckboxChange}
                                  className="me-1"
                                />
                                Enable BGP
                              </Label>
                            </FormGroup>
                            <FormGroup className="form-item">
                              <Label for="bgpTextarea">BGP Details</Label>
                              <Input
                                type="textarea"
                                id="bgpTextarea"
                                value={routingConfig?.bgp_configuration} 
                                onChange={this.handleBGPTextChange} 
                                disabled={!routingConfig?.bgp_enabled}
                              />
                            </FormGroup>
                          </TabPane>
                        </TabContent>   
                      </CardBody>
                    </CustomCard>
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col lg={12} md={12} sm={12} xs={12}>
                <div className="controllers-card-wrapper d-flex">
                    <CustomCard>
                      <CardHeader className="p-2 fw-bold d-flex align-items-center bg-transparent">
                        <div className="w-100 d-flex justify-content-between">       
                            <IconHeading icon={<MdAccountTree className="icon-medium" />} heading="Gateways" />

                            <div className="pointer add-section" onClick={this.addServer}>
                              <PlusIcon /> &nbsp;Add Gateway
                            </div>
                        </div>
                      </CardHeader>
                      <CardBody>
                        <Table
                          onRowEdit={this.updateEditableRows}
                          aria-label="Edit Gateways"
                          variant={TableVariant.compact}
                          cells={this.getServerColumns()}
                          rows={servers}
                        >
                          <TableHeader />
                          <TableBody />
                        </Table>
                        <div className="text-center">
                          <MdAddCircle className="controller-add-icon" onClick={this.addServer}/>
                        </div>
                        <br />
                        <span className="error">{this.state.error}</span>
                      </CardBody>
                    </CustomCard>
                </div>
              </Col>
            </Row>

          <Flex>
            <FlexItem className="flex-item-full d-flex justify-content-center">

              <Saver 
                submitButtonText="Save" 
                submitButtonisDisabled={formInvalid}
                submit={this.handleSubmit} 
                cancel={this.handleCancel} 
                showCancel={true}
                message={this.state.message}
                messageType={this.state.messageType}
              />
            </FlexItem>
          </Flex>
          
          </Page>
        )}
      </React.Fragment>
    );
  }
});

export default withRouter(ControllerForm);
